import {
  type InventoryKgItem,
  conversationInventoryKnowledgeGraphKey,
} from '@toggle/toggle';
import { t } from 'i18next';

import { postEntity } from '~/services/entities/entity-service';
import {
  fetchCompanyCompetitorsWithThemes,
  fetchCompanyCountryExposure,
  fetchCompanyRegionExposure,
  fetchCompanyTheme,
  fetchCompanyThemeCompetitors,
  fetchTheme,
  fetchThemeCompanies,
  getBrandsAndProducts,
} from '~/services/overview-widget/overview-widget-service';

import { KG_SUBSECTION_MAX_CHILDREN } from '../knowledge-graph-inventory/groupKgInventoryItems';
import {
  type ResolvedKgInventoryRow,
  resolveKgInventoryRows,
} from '../knowledge-graph-inventory/resolveKgInventoryRows';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';

export type KgExportGroup = { rootLine: string; childLines: string[] };

export type KgForSourcesSection = {
  kgExportGroups: KgExportGroup[] | null;
};

const EXPORT_FETCH_LIMIT = 10;

function collectThemeIdsFromKgItems(items: InventoryKgItem[]): string[] {
  const ids = new Set<string>();

  for (const item of items) {
    if (item.node_type === 'theme') {
      ids.add(item.node_key);
    }
  }

  return [...ids];
}

export function collectKgThemeIdsForExport(
  inventory:
    | Pick<ConversationInventory, typeof conversationInventoryKnowledgeGraphKey>
    | undefined
): string[] {
  const items = inventory?.[conversationInventoryKnowledgeGraphKey]?.items;

  if (!items?.length) {
    return [];
  }

  return collectThemeIdsFromKgItems(items);
}

async function fetchThemeDisplayNamesForKgItems(
  items: InventoryKgItem[]
): Promise<Record<string, string>> {
  const ids = collectThemeIdsFromKgItems(items);

  if (ids.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    ids.map(async id => {
      const data = await fetchTheme({ themeId: id });
      return [id, data.name];
    })
  );
  return Object.fromEntries(entries);
}

async function entityDisplayName(tag: string): Promise<string> {
  const res = await postEntity(tag);
  const e = res.data;
  return e?.name_short || e?.name_full || tag;
}

async function rowToExportGroup(
  row: ResolvedKgInventoryRow,
  themeDisplayNames: Record<string, string>
): Promise<KgExportGroup> {
  switch (row.kind) {
    case 'theme-company': {
      const themeId = row.themeId;
      const rootLine =
        themeDisplayNames[themeId] ?? (await fetchTheme({ themeId })).name;
      const companies = await fetchThemeCompanies({
        themeId,
        limit: EXPORT_FETCH_LIMIT,
      });
      const slice = companies.slice(0, KG_SUBSECTION_MAX_CHILDREN);
      const childLines = await Promise.all(
        slice.map(tc => entityDisplayName(tc.company))
      );
      return { rootLine, childLines };
    }

    case 'company-theme': {
      const tag = row.entityTag;
      const parentName = await entityDisplayName(tag);
      const rootLine = t('chat:inventory.kg.companyThemeExposures', {
        name: parentName,
      });
      const exposures = await fetchCompanyTheme({
        entityTag: tag,
        limit: EXPORT_FETCH_LIMIT,
      });
      const childLines = exposures
        .slice(0, KG_SUBSECTION_MAX_CHILDREN)
        .map(ex => ex.theme.name);
      return { rootLine, childLines };
    }

    case 'company-brand': {
      const tag = row.entityTag;
      const parentName = await entityDisplayName(tag);
      const rootLine = t('chat:inventory.kg.companyBrandsAndProducts', {
        name: parentName,
      });
      const brands = await getBrandsAndProducts({ entityTag: tag });
      const childLines = brands
        .slice(0, KG_SUBSECTION_MAX_CHILDREN)
        .map(b => b.product);
      return { rootLine, childLines };
    }

    case 'company-competitor': {
      const tag = row.entityTag;
      const parentName = await entityDisplayName(tag);
      const themeFilterId = row.themeFilterId;
      let competitorTags: string[];

      if (themeFilterId) {
        const resp = await fetchCompanyThemeCompetitors({
          entityTag: tag,
          themeId: themeFilterId,
          limit: EXPORT_FETCH_LIMIT,
        });
        competitorTags = resp.competitors;
      } else {
        const rows = await fetchCompanyCompetitorsWithThemes({
          entityTag: tag,
          limit: EXPORT_FETCH_LIMIT,
        });
        competitorTags = rows.map(r => r.company);
      }

      const visibleTags = competitorTags.slice(0, KG_SUBSECTION_MAX_CHILDREN);
      const titleCount = Math.min(
        competitorTags.length,
        KG_SUBSECTION_MAX_CHILDREN
      );
      const rootLine = themeFilterId
        ? t('chat:inventory.kg.topNCompetitorsForTheme', {
            name: parentName,
            count: titleCount,
            theme: themeDisplayNames[themeFilterId] ?? themeFilterId,
          })
        : t('chat:inventory.kg.topNCompetitors', {
            name: parentName,
            count: titleCount,
          });
      const childLines = await Promise.all(
        visibleTags.map(ct => entityDisplayName(ct))
      );
      return { rootLine, childLines };
    }

    case 'company-geography': {
      const tag = row.entityTag;
      const parentName = await entityDisplayName(tag);
      const rootLine = t('chat:inventory.kg.companyGeographicExposures', {
        name: parentName,
      });
      const [countries, regions] = await Promise.all([
        fetchCompanyCountryExposure({ entityTag: tag }),
        fetchCompanyRegionExposure({ entityTag: tag }),
      ]);
      const childLines: string[] = [];

      for (const c of countries) {
        if (childLines.length >= KG_SUBSECTION_MAX_CHILDREN) {
          break;
        }

        childLines.push(
          t(`countries:${c.code.toUpperCase()}`, {
            defaultValue: c.code,
          })
        );
      }

      for (const r of regions) {
        if (childLines.length >= KG_SUBSECTION_MAX_CHILDREN) {
          break;
        }

        childLines.push(r.name);
      }

      return { rootLine, childLines };
    }
  }
}

export async function resolveKgForSourcesSection(
  inventory: ConversationInventory | FormattedConversationInventory | undefined
): Promise<KgForSourcesSection> {
  const items = inventory?.[conversationInventoryKnowledgeGraphKey]?.items;

  if (!items?.length) {
    return { kgExportGroups: null };
  }

  try {
    const rows = resolveKgInventoryRows(items);

    if (rows.length === 0) {
      return { kgExportGroups: null };
    }

    const themeDisplayNames = await fetchThemeDisplayNamesForKgItems(items);
    const kgExportGroups = await Promise.all(
      rows.map(row => rowToExportGroup(row, themeDisplayNames))
    );
    return { kgExportGroups };
  } catch {
    return { kgExportGroups: null };
  }
}

export const kgExportGroupsToPlainText = (groups: KgExportGroup[]): string =>
  groups
    .map(g => {
      const lines = [`- ${g.rootLine}`];

      for (const c of g.childLines) {
        lines.push(`  - ${c}`);
      }

      return lines.join('\n');
    })
    .join('\n');

export const kgExportGroupsToHtml = (groups: KgExportGroup[]): string => {
  const items = groups
    .map(g => {
      const childUl =
        g.childLines.length > 0
          ? htmlNodes.unorderedList(
              g.childLines.map(c => htmlNodes.unorderedListItem(c)).join('')
            )
          : '';
      return `<li>${g.rootLine}${childUl}</li>`;
    })
    .join('');
  return htmlNodes.unorderedList(items);
};
